import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { IMaskInput } from 'react-imask';

// Метатеги
const MetaTags = () => (
  <>
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta name="description" content="Добро пожаловать в Сонет-Дом! Здесь вы можете узнать больше о наших услугах и оставить отзывы." />
    <meta name="keywords" content="Сонет-Дом, личный кабинет, оплата услуг, отзывы" />
  </>
);

// Стили для видеофона
const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.7);
  z-index: -1;
`;

// Стиль для секции
const Section = styled(motion.section)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  overflow: hidden;
  position: relative;
  background: ${(props) => props.bgColor || "linear-gradient(135deg, #ffffff, #e0f7fa)"};
  color: ${(props) => (props.textColor || "black")};
  transition: background 0.5s ease;
  h1,
  p,
  button {
    z-index: 1;
    position: relative;
    text-align: center;
  }
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

// Специальный стиль для секции директора
const DirectorSection = styled(Section)`
  padding: 2rem 0;
  background: linear-gradient(to bottom, #ffffff, #f5f5f5);
  min-height: auto;
`;

const MessageBoard = styled.div`
  width: 90%;
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const LoginForm = styled.form`
  display: flex;
  gap: 1rem;
  align-items: center;
  
  input {
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
  
  button {
    padding: 0.5rem 1rem;
    background: #007aff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
`;

const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const UserMessage = styled.div`
  background: #f4f4f4;
  padding: 1.5rem;
  border-radius: 12px;
  position: relative;
`;

const AdminMessage = styled.div`
  background: #e0f7fa;
  padding: 1.5rem;
  border-radius: 12px;
  margin-left: 3rem;
  position: relative;
`;

const MessageMeta = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #666;
`;

const MessageText = styled.p`
  margin: 0;
  white-space: pre-wrap;
`;

const ReplyForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
  
  textarea {
    padding: 0.8rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    min-height: 100px;
  }
  
  button {
    align-self: flex-end;
    padding: 0.5rem 1rem;
    background: #007aff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
`;

// Стили для Swiper
const CustomSwiper = styled(Swiper)`
  width: 90%;
  max-width: 800px;
  margin: 2rem auto;
  padding: 20px 0 40px;
  .swiper-slide {
    display: flex;
    justify-content: center;
  }
  .swiper-pagination-bullet {
    background: #007aff;
  }
  .swiper-button-next, 
  .swiper-button-prev {
    color: #007aff;
  }
`;

// Заголовок
const Title = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 2rem;
  text-align: center;
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// Кнопка
const Button = styled(motion.button)`
  padding: 1rem 1.1rem;
  font-size: 0.8rem;
  color: white;
  background-color: #007aff;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: transform 0.3s ease;
  margin: 0.5rem;
  &:hover {
    transform: scale(1.1);
  }
  @media (max-width: 768px) {
    padding: 0.8rem 0.9rem;
    font-size: 0.8rem;
  }
`;

// Стили для контейнера новостей/ремонтных работ
const NewsContainer = styled.div`
  width: 80%;
  max-width: 1200px;
  margin: 2rem auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

// Стили для карточек новостей/ремонтных работ
const NewsItem = styled(motion.div)`
  background: #f4f4f4;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }
  h3 {
    margin: 0 0 0.5rem;
    font-size: 1.2rem;
    font-weight: bold;
    color: #333;
  }
  p {
    margin: 0;
    font-size: 1rem;
    color: #555;
  }
  small {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: #888;
    text-align: right;
  }
`;

// Стили для карточек ремонтных работ (белый фон)
const RepairItem = styled(NewsItem)`
  background: #fff;
  border: 1px solid #e0e0e0;
`;

// Стили для сообщений
const MessageContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  width: 100%;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #007aff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-right: 1rem;
  flex-shrink: 0;
`;

const Logo = styled.img`
  width: 40px;
  height: 40px;
  margin-right: 1rem;
  flex-shrink: 0;
`;

const Bubble = styled.div`
  background-color: #f4f4f4;
  padding: 1rem;
  border-radius: 12px;
  position: relative;
  max-width: 70%;
`;

const BubbleAfter = styled.div`
  content: "";
  position: absolute;
  top: 10px;
  left: -10px;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-right: 10px solid #f4f4f4;
  border-bottom: 10px solid transparent;
`;

const BubbleContent = styled.div`
  margin-bottom: 0.5rem;
`;

const BubbleDate = styled.small`
  display: block;
  font-size: 0.8rem;
  color: #888;
`;

const AnswerBubble = styled(Bubble)`
  background-color: #e0f7fa;
  margin-left: 5rem;
`;

const AnswerBubbleAfter = styled(BubbleAfter)`
  left: auto;
  right: -10px;
  border-right: 10px solid transparent;
  border-left: 10px solid #e0f7fa;
`;

// Стили для формы
const FormContainer = styled.div`
  width: 90%;
  max-width: 600px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const FormInput = styled.input`
  padding: 0.8rem 1rem;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
`;

const FormTextarea = styled.textarea`
  padding: 0.8rem 1rem;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: vertical;
  width: 100%;
  min-height: 120px;
`;

const Home = () => {

  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [adminLogin, setAdminLogin] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  
  const handleAdminLogin = (e) => {
    e.preventDefault();
    // Здесь должна быть проверка логина/пароля
    if (adminLogin === 'admin' && adminPassword === 'password') {
      setIsAdminLoggedIn(true);
      setAdminLogin('');
      setAdminPassword('');
    } else {
      alert('Неверные учетные данные');
    }
  };
  
  const handleAdminLogout = () => {
    setIsAdminLoggedIn(false);
    setReplyingTo(null);
  };
  
  const handleReplySubmit = (e, messageId) => {
    e.preventDefault();
    if (!replyText.trim()) return;
  
    const updatedMessages = messages.map(msg => {
      if (msg.id === messageId) {
        return {
          ...msg,
          answer: replyText,
          answerDate: new Date().toLocaleDateString('ru-RU'),
          showAnswer: true
        };
      }
      return msg;
    });
  
    setMessages(updatedMessages);
    localStorage.setItem('userMessages', JSON.stringify(updatedMessages));
    setReplyingTo(null);
    setReplyText('');
    
    // Здесь должна быть логика сохранения ответа на сервере
    axios.post('/api/save-reply', { messageId, replyText })
      .catch(error => console.error('Error saving reply:', error));
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const [news, setNews] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [visibleNewsCount, setVisibleNewsCount] = useState(3);
  const [visibleRepairsCount, setVisibleRepairsCount] = useState(3);
  const [messages, setMessages] = useState([]);
  const [accountNumber, setAccountNumber] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [question, setQuestion] = useState("");
  

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchNews();
    fetchRepairs();
    fetchMessages();
    // Загрузка сообщений из localStorage
    const savedMessages = localStorage.getItem('userMessages');
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    }
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get("/data/news.json");
      const sortedNews = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setNews(sortedNews);
    } catch (error) {
      console.error("Error fetching news:", error);
    }
  };

  const fetchRepairs = async () => {
    try {
      const response = await axios.get("/data/rem.json");
      setRepairs(response.data);
    } catch (error) {
      console.error("Error fetching repairs:", error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get("/data/otvet.json");
      setMessages(response.data.messages);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const showMoreNews = () => {
    setVisibleNewsCount(news.length);
  };

  const hideNews = () => {
    setVisibleNewsCount(3);
  };

  const showMoreRepairs = () => {
    setVisibleRepairsCount(repairs.length);
  };

  const hideRepairs = () => {
    setVisibleRepairsCount(3);
  };

  const toggleShowText = (messageId) => {
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg.id === messageId ? { ...msg, showAnswer: !msg.showAnswer } : msg
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d+$/.test(accountNumber)) {
      alert('Лицевой счет должен содержать только цифры.');
      return;
    }
    if (!/^[A-Za-zА-Яа-я\s]+$/.test(name)) {
      alert('Имя должно содержать только буквы и пробелы.');
      return;
    }
    if (question.length > 300) {
      alert('Вопрос не должен содержать более 300 символов.');
      return;
    }
    if (!accountNumber || !name || !phone || !question) return;
    try {
      const newMessage = {
        id: Date.now(),
        accountNumber,
        name,
        phone,
        text: question,
        date: new Date().toLocaleDateString('ru-RU'),
        isDirector: false,
        answer: null,
        answerDate: null,
        showAnswer: false,
      };
      // Обновляем локальное состояние
      const updatedMessages = [...messages, newMessage];
      setMessages(updatedMessages);
      // Сбрасываем форму
      setAccountNumber("");
      setName("");
      setPhone("");
      setQuestion("");
      // Сохраняем в localStorage
      localStorage.setItem('userMessages', JSON.stringify(updatedMessages));
      // Читаем текущий otvet.json
      const response = await axios.get("/data/otvet.json");
      const currentData = response.data;
      // Добавляем новое сообщение
      currentData.messages.push(newMessage);
      // Перезаписываем otvet.json
      axios.post("/data/otvet.json", currentData, {
        headers: {
          'Content-Type': 'application/json'
        }
      }).then(() => {
        alert('Ваше сообщение отправлено!');
      }).catch(error => {
        console.error("Error updating otvet.json:", error);
        alert('Произошла ошибка при отправке сообщения');
      });
    } catch (error) {
      console.error("Error submitting message:", error);
      alert('Произошла ошибка при отправке сообщения');
    }
  };

  return (
    <>
      <MetaTags />
      <main>
        {/* Секция 1: Видеофон */}
        <Section
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          bgColor="transparent"
          textColor="white"
        >
          <VideoBackground autoPlay loop muted playsInline>
            <source src="/vecteezy_white-background-stripe-curve-wave-4k-resolution-clean_14415197.mp4" type="video/mp4" />
            Ваш браузер не поддерживает видео.
          </VideoBackground>
          <header>
            <Title>Добро пожаловать в Сонет-Дом!</Title>
          </header>
          <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '400px' }}>
            <Button whileTap={{ scale: 0.9 }} onClick={() => window.location.href = "https://sonet-dom.ru/stat/"} className="personal-account">
              Личный кабинет
            </Button>
            <Button whileTap={{ scale: 0.9 }} onClick={() => window.location.href = "https://sonet-dom.ru/oplata/"} className="payment-button">
              Оплатить услуги
            </Button>
            <Button whileTap={{ scale: 0.9 }} onClick={() => window.location.href = "https://sonet-dom.ru/application/"} className="application-button">
              Оставить заявку
            </Button>
          </nav>
        </Section>

                {/* Секция 2.5: Приложение Открывашка */}
                {/* Секция 2.5: Приложение Открывашка */}
        <Section
  variants={sectionVariants}
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true }}
  bgColor="#e6f7ff"
  textColor="black"
  style={{
    padding: '4rem 1rem',
    minHeight: 'auto',
    backgroundImage: `url('/sonet-dom/public/1766650328.png')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    overflow: 'hidden',
  }}
>
  {/* Фоновое изображение с размытием */}
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    zIndex: 0,
    backdropFilter: 'blur(20px)',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  }}>
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `url('/sonet-dom/public/1766650328.png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      opacity: 0.9,
    }} />
  </div>

  {/* Анимированный фон с пузырями */}
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    zIndex: 0,
  }}>
    {[...Array(8)].map((_, i) => (
      <motion.div
        key={i}
        style={{
          position: 'absolute',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          // Размытие убрано: filter: 'blur(20px)' удалено
        }}
        initial={{
          x: Math.random() * 100,
          y: Math.random() * 100,
          scale: Math.random() * 0.5 + 0.5,
        }}
        animate={{
          x: [null, Math.random() * 100 - 50],
          y: [null, Math.random() * 100 - 50],
        }}
        transition={{
          duration: Math.random() * 10 + 10,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut",
        }}
      />
    ))}
  </div>

  <div style={{
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '1300px',
    width: '100%',
    gap: '3rem',
    flexWrap: 'wrap',
    position: 'relative',
    zIndex: 1,
  }}>

    {/* Левая часть: Текст и кнопки с прозрачными облачками */}
    <div style={{
      flex: 1,
      minWidth: '300px',
    }}>
      {/* Заголовок с анимацией */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 8px 32px rgba(0, 122, 255, 0.2)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        }}
      >
        <h2 style={{
          fontSize: '3rem',
          marginBottom: '0.5rem',
          background: 'linear-gradient(45deg, #007aff, #00bfff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: '800',
        }}>
          Забудьте про ключи.
        </h2>

        <h3 style={{
          fontSize: '2rem',
          marginBottom: '1rem',
          color: '#333',
          fontWeight: '600',
        }}>
          Привет, будущее!
        </h3>

        <p style={{
          fontSize: '1.2rem',
          lineHeight: '1.8',
          color: '#555',
          marginBottom: '1.5rem',
        }}>
          С «Открывашкой» от Сонет-Дом вы открываете подъезд в один клик и видите всё, что происходит у двери — даже за тысячи километров от дома.
        </p>
      </motion.div>

      {/* Функции приложения с иконками */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
        style={{
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 8px 32px rgba(0, 122, 255, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.25)',
        }}
      >
        {/* Анимированные иконки функций */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {[
            { icon: '🎥', title: 'Смотрите живое видео с подъезда', text: 'Кто пришёл, что происходит — всё под контролем 24/7.' },
            { icon: '🔑', title: 'Откройте подъезд одним касанием', text: 'Даже если вы на работе, в отпуске или просто забыли ключи.' },
            { icon: '🛡️', title: 'Безопасно, удобно, мгновенно', text: 'Всё под рукой, без задержек и бумажек.' }
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem',
                padding: '1rem',
                borderRadius: '15px',
                background: 'rgba(255, 255, 255, 0.5)',
                transition: 'all 0.3s ease',
              }}
              whileHover={{
                scale: 1.02,
                background: 'rgba(0, 122, 255, 0.1)',
                boxShadow: '0 4px 20px rgba(0, 122, 255, 0.2)'
              }}
            >
              <motion.span
                style={{ fontSize: '2rem', flexShrink: 0 }}
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 5 }}
              >
                {item.icon}
              </motion.span>
              <div>
                <h4 style={{
                  margin: '0 0 5px 0',
                  color: '#007aff',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  {item.title}
                </h4>
                <p style={{
                  margin: 0,
                  color: '#666',
                  fontSize: '0.95rem',
                  lineHeight: '1.5'
                }}>
                  {item.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Слоган и кнопки */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        viewport={{ once: true }}
        style={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '2.5rem',
          boxShadow: '0 8px 32px rgba(0, 122, 255, 0.25)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          textAlign: 'center',
        }}
      >
        <motion.h3
          style={{
            fontSize: '2.2rem',
            marginBottom: '1.5rem',
            background: 'linear-gradient(45deg, #007aff, #00bfff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            // Курсив убран: fontStyle: 'italic' удалено
            fontWeight: '700',
          }}
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          «Открывашка» — ваш дом в кармане.
        </motion.h3>

        <motion.p
          style={{
            fontSize: '1.3rem',
            marginBottom: '2rem',
            fontWeight: '600',
            color: '#333',
          }}
          animate={{ opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Скачайте сейчас и почувствуйте свободу!
        </motion.p>

        {/* Анимированные кнопки для скачивания */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '1.5rem',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          {/* Android кнопка с анимацией */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <motion.div
              style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
              }}
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
            />
            <Button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                backgroundColor: '#32CD32',
                padding: '1.2rem 2.5rem',
                borderRadius: '15px',
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontWeight: '600',
                position: 'relative',
                zIndex: 1,
                border: 'none',
                boxShadow: '0 6px 20px rgba(50, 205, 50, 0.4)',
              }}
              onClick={() => window.location.href = '/sonet-dom1-1.apk'}
            >
              <motion.svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="white"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              >
                <path d="M3 20.5V3.5C3 2.67 3.67 2 4.5 2H19.5C20.33 2 21 2.67 21 3.5V20.5C21 21.33 20.33 22 19.5 22H4.5C3.67 22 3 21.33 3 20.5ZM7 19H17V5H7V19Z"/>
              </motion.svg>
              Скачать для Android
            </Button>
          </motion.div>

          {/* iOS кнопка */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              style={{
                backgroundColor: '#888',
                padding: '1.2rem 2.5rem',
                borderRadius: '15px',
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontWeight: '600',
                position: 'relative',
                cursor: 'not-allowed',
                border: 'none',
                boxShadow: '0 6px 20px rgba(136, 136, 136, 0.3)',
                opacity: 0.8,
              }}
              disabled
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                <path d="M18.71 19.5C17.88 20.74 17 21.5 15.66 21.5C14.33 21.5 13.5 20.75 12.47 20.75C11.45 20.75 10.46 21.5 9.34 21.5C8 21.5 7 20.75 6.34 19.5C4.71 16.5 5.5 12 8.5 12C9.5 12 10.5 12.5 11.5 12.5C12.5 12.5 13.5 12 14.5 12C15.5 12 16.5 12.5 17.5 12.5C20.5 12.5 21.29 16.5 19.66 19.5H18.71ZM15.5 10.5C16.33 10.5 17.17 9.83 17.5 9C17.83 8.17 18.33 7.17 18.5 6C17.67 6 16.83 6.67 16.5 7.5C16.17 8.33 15.67 9.33 15.5 10.5ZM12 3C13.5 3 15 4.5 15 6.5C15 8.5 13.5 10 12 10C10.5 10 9 8.5 9 6.5C9 4.5 10.5 3 12 3Z"/>
              </svg>
              <div style={{ textAlign: 'left' }}>
                <div>Для iOS</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>(в разработке)</div>
              </div>
            </Button>
          </motion.div>
        </div>
      </motion.div>
    </div>

    {/* Правая часть: Анимированный телефон */}
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1, delay: 0.3 }}
      viewport={{ once: true }}
      style={{
        flex: 1,
        minWidth: '300px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      {/* Эффект свечения вокруг телефона */}
      <motion.div
        style={{
          position: 'absolute',
          width: '450px',
          height: '900px',
          background: 'radial-gradient(circle, rgba(0, 122, 255, 0.2) 0%, transparent 70%)',
          borderRadius: '60px',
          filter: 'blur(30px)',
        }}
        animate={{ opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity }}
      />

      {/* Анимированный телефон */}
      <motion.div
        style={{
          position: 'relative',
          width: '367px',
          height: '760px',
          perspective: '1000px',
        }}
        animate={{
          y: [0, -20, 0],
          rotateY: [0, 5, -5, 0]
        }}
        transition={{
          y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
          rotateY: { duration: 6, repeat: Infinity, ease: "easeInOut" }
        }}
      >
        <img
          src="/phone.png"
          alt="Приложение Открывашка"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            filter: 'drop-shadow(0 20px 40px rgba(0, 122, 255, 0.4))',
            borderRadius: '40px',
          }}
        />

        {/* Анимированные элементы на экране телефона */}
        <div style={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          right: '10%',
          bottom: '15%',
          background: 'rgba(255, 255, 255, 0.1)', // Увеличил прозрачность для лучшей видимости
          borderRadius: '25px',
          overflow: 'hidden',
          // backdropFilter: 'blur(5px)', // Убрал размытие
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}>
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                background: 'rgba(0, 122, 255, 0.3)', // Увеличил прозрачность
                borderRadius: '10px',
                left: `${10 + i * 30}%`,
                top: `${20 + i * 25}%`,
                width: '60%',
                height: '15%',
              }}
              animate={{ opacity: [0.2, 0.4, 0.2] }} // Увеличил значения для лучшей видимости
              transition={{ duration: 2, delay: i * 0.5, repeat: Infinity }}
            />
          ))}
        </div>
      </motion.div>

      {/* Плавающие иконки вокруг телефона */}
      {['🔑', '📱', '🎥', '👁️', '🛡️'].map((icon, i) => (
        <motion.div
          key={icon}
          style={{
            position: 'absolute',
            fontSize: '2rem',
            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))',
          }}
          initial={{
            x: Math.cos(i * 72 * Math.PI / 180) * 200,
            y: Math.sin(i * 72 * Math.PI / 180) * 200,
            scale: 0,
          }}
          animate={{
            x: Math.cos(i * 72 * Math.PI / 180) * 200,
            y: Math.sin(i * 72 * Math.PI / 180) * 200,
            scale: [0, 1, 0],
            rotate: [0, 360],
          }}
          transition={{
            scale: { duration: 3, delay: i * 0.3, repeat: Infinity },
            rotate: { duration: 10, repeat: Infinity, ease: "linear" },
          }}
        >
          {icon}
        </motion.div>
      ))}
    </motion.div>
  </div>
</Section>

        {/* Секция 2: Новости */}
        <Section
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          bgColor="#fff"
          textColor="black"
        >
          <Title>Новости</Title>
          <NewsContainer>
            {news.slice(0, visibleNewsCount).map((item, index) => (
              <NewsItem
                key={index}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
              >
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <small>{item.date}</small>
              </NewsItem>
            ))}
            <div style={{ display: "flex", gap: "1rem" }}>
              {visibleNewsCount < news.length && (
                <Button whileTap={{ scale: 0.9 }} onClick={showMoreNews}>
                  Ещё
                </Button>
              )}
              {visibleNewsCount === news.length && (
                <Button whileTap={{ scale: 0.9 }} onClick={hideNews}>
                  Скрыть
                </Button>
              )}
            </div>
          </NewsContainer>
        </Section>

        {/* Секция 3: Ремонтные работы */}
        <Section
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          bgColor="#f9f9f9"
          textColor="black"
        >
          <Title>Ремонтные работы</Title>
          <NewsContainer>
            {repairs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                На данный момент нет масштабных неисправностей подъездов. На страже дверей всегда ваш Сонет-Дом 😊
              </div>
            ) : (
              <>
                {repairs.slice(0, visibleRepairsCount).map((item, index) => (
                  <RepairItem
                    key={index}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <h3>{item.address}</h3>
                    <p><strong>Проблема:</strong> {item.issue}</p>
                    <small>
                      <strong>Период работ:</strong> {item.start_date} - {item.end_date}
                    </small>
                  </RepairItem>
                ))}
                <div style={{ display: "flex", gap: "1rem" }}>
                  {visibleRepairsCount < repairs.length && (
                    <Button whileTap={{ scale: 0.9 }} onClick={showMoreRepairs}>
                      Ещё
                    </Button>
                  )}
                  {visibleRepairsCount === repairs.length && (
                    <Button whileTap={{ scale: 0.9 }} onClick={hideRepairs}>
                      Скрыть
                    </Button>
                  )}
                </div>
              </>
            )}
          </NewsContainer>
        </Section>


      </main>
    </>
  );
};

export default Home;